import {
  Button,
  Card,
  Checkbox,
  Descriptions,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  Select,
  Table,
  Tag,
  theme,
  Typography,
} from 'antd';
import { ChevronDown, LoaderCircle } from 'lucide-react';
import React, { type ReactNode, useEffect, useMemo, useState, useRef } from 'react';
import {
  catchError,
  concatMap,
  EMPTY,
  from,
  of,
  switchMap,
  tap,
  throwError,
  debounceTime,
  map,
  Subject,
} from 'rxjs';
import {
  FoodGroup,
  Ingredient,
  MealItem,
  MealItemIngredient,
  Measure,
  type ServingTool,
} from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import {
  AtomicOperation,
  useAtomicOperations,
  useMutation,
  useQuery,
} from '../../../jsonapi-provider';
import { randomUUID } from '../../../utils/utils';

const SUCCESS_ADD_MESSAGE = 'Ingredient added successfully';
const SUCCESS_UPDATE_MESSAGE = 'Ingredient updated successfully';

interface IngredientFormProps {
  ingredient: Ingredient;
  onSuccess: (ingredient: Ingredient, newIngrediant: boolean) => void;
  onCancel: () => void;
}

const USDAFoodSelectOptionsLabel = ({ food }: { food: any }): ReactNode => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <Typography.Text style={{ color: 'inherit' }}>
      {food.brandName && (
        <>
          <b>{food.brandName}</b>
          <br />
        </>
      )}
      {food.gtinUpc ? food.gtinUpc + ' - ' : ''}
      {food.description}
      {food.servingSize ? ' - ' + food.servingSize.toFixed(2) + ' ' + food.servingSizeUnit : ''}
    </Typography.Text>
  </div>
);

const IngredientForm = (props: IngredientFormProps): ReactNode => {
  const { token } = theme.useToken();
  const notification = useNotification();
  const [formInstance] = Form.useForm();
  const { ingredient, onSuccess, onCancel } = props;
  const isEditMode = useMemo(() => !!ingredient.id, [ingredient.id]);
  const initialValues = useMemo(
    () => ({
      ...ingredient,
      foodGroupId: ingredient.foodGroup.id,
    }),
    [ingredient]
  );
  const atomicOperations = useAtomicOperations();
  const foodGroups = useQuery<FoodGroup[]>([
    'food-group',
    { page: { limit: 10000 }, sort: 'name' },
  ]);
  const measures = useQuery<Measure[]>([
    'measure',
    { page: { limit: 10000 }, sort: 'name', fields: { measure: ['name'] } },
  ]);
  const servingTools = useQuery<ServingTool[]>([
    'serving-tool',
    {
      page: {
        totals: 1,
        number: 1,
        size: 10000,
      },
      fields: { 'serving-tool': ['name'] },
    },
  ]);
  const [mutateIngredient, ingredients] = useMutation<Ingredient>(
    ingredient?.id ? ['ingredient', ingredient.id] : ['ingredient']
  );
  const [selectedUsdaFood, setSelectedUsdaFood] = useState<any>(null);
  const [usdaSearching, setUsdaSearching] = useState(false);
  const [usdaFoodOptions, setUsdaFoodOptions] = useState<any[]>([]);
  const searchUSDAFood = useRef(new Subject<string>());

  useEffect(() => {
    setSelectedUsdaFood(ingredient.usdaFood);
    if (ingredient.usdaFood) {
      setUsdaFoodOptions(
        [ingredient.usdaFood].map((item) => ({
          label: <USDAFoodSelectOptionsLabel food={item} />,
          value: item.fdcId,
          ...item,
        }))
      );
    }
  }, [ingredient]);

  useEffect(() => {
    const subscriber = searchUSDAFood.current
      .pipe(
        debounceTime(1500),
        tap(() => {
          setUsdaSearching(true);
        }),
        concatMap((query) =>
          from(
            fetch(
              `${process.env.REACT_APP_USDA_FOOD_SEARCH_API}?query=${encodeURIComponent(
                query
              )}&pageSize=7&api_key=${process.env.REACT_APP_USDA_API_KEY}`
            )
          )
        ),
        concatMap((res) =>
          res.ok
            ? from(res.json())
            : from(res.json()).pipe(
                concatMap((json: any) => throwError(() => new Error(json.error.message)))
              )
        ),
        map((res) => {
          const results = Array.isArray(res.foods) ? res.foods : [];
          return results.map((food: any) => ({
            label: <USDAFoodSelectOptionsLabel food={food} />,
            value: food.fdcId,
            ...food,
          }));
        }),
        tap((results) => {
          setUsdaFoodOptions(results);
        }),
        catchError((err: Error) => {
          formInstance.setFields([
            {
              name: 'usdaFood',
              errors: [err.message],
            },
          ]);
          return EMPTY;
        }),
        tap(() => {
          setUsdaSearching(false);
        })
      )
      .subscribe();

    return () => {
      subscriber.unsubscribe();
    };
  }, []);

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const includeMealItem = (submittedIngredient: Ingredient, newIngredient: boolean): void => {
    const mealItemToBeSaved = new MealItem(
      submittedIngredient.id,
      submittedIngredient.name,
      measures.data?.[0],
      measures.data?.[0],
      1.0,
      [],
      servingTools.data?.[0],
      1.0
    );

    // @ts-expect-error operand of delete operator must be optional
    delete mealItemToBeSaved.mealItemIngredients;

    const mealItemOperation: AtomicOperation<MealItem> = AtomicOperation.add(
      mealItemToBeSaved,
      randomUUID()
    );
    const ingredientOperation: AtomicOperation<Ingredient> = AtomicOperation.add(
      submittedIngredient,
      randomUUID()
    );

    const miis = [
      new MealItemIngredient(
        ingredient.id,
        new Ingredient(ingredientOperation.lid),
        1,
        new Measure('1'),
        new MealItem(mealItemOperation.lid),
        0
      ),
    ];

    const mealItemIngredientsToAdd = miis
      .filter((mii) => !mii.id)
      .map((mii) =>
        AtomicOperation.add<MealItemIngredient>(mii, undefined, [
          'meal-item',
          mii.mealItem.id!,
          'mealItemIngredients',
          mii.id!,
        ])
      );

    atomicOperations
      .operate(
        Array.of<AtomicOperation<MealItem | Ingredient | MealItemIngredient>>(
          mealItemOperation,
          ingredientOperation,
          ...mealItemIngredientsToAdd
        )
      )
      .pipe(
        tap(() => {
          notification.success({
            message: ' ',
            description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
          });
        }),
        tap((results) => {
          onSuccess(results[1] as Ingredient, newIngredient);
        }),
        catchError((error: Error) => {
          notification.error({
            message: ' ',
            description: error.message,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };
  const onSubmit = (
    submitted: Ingredient & {
      foodGroupId: FoodGroup['id'];
      addMealItem: boolean;
      newIngredient: boolean;
    }
  ): void => {
    const ingredient = new Ingredient(
      submitted.id,
      submitted.name,
      submitted.description,
      submitted.diets,
      new FoodGroup(submitted.foodGroupId),
      selectedUsdaFood
        ? Object.assign({}, selectedUsdaFood, { label: undefined, value: undefined })
        : undefined
    );
    if (submitted.addMealItem) {
      includeMealItem(ingredient, submitted.newIngredient);
    } else
      from(mutateIngredient(ingredient))
        .pipe(
          switchMap((res) =>
            res.error ? throwError(() => new Error(res.error?.title)) : of(res.data!)
          ),
          tap(() => {
            notification.success({
              message: ' ',
              description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
            });
          }),
          tap((ingredient) => {
            onSuccess(ingredient, submitted.newIngredient);
          }),
          catchError((error: Error) => {
            notification.error({
              message: '',
              description: error.message,
            });
            return EMPTY;
          })
        )
        .subscribe();
  };

  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="ingredient-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>
              {isEditMode ? ingredient.name : 'New Ingredient'}
            </Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Food group" name="foodGroupId" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="name"
              defaultActiveFirstOption
              options={foodGroups.data}
              fieldNames={{ label: 'name', value: 'id' }}
              disabled={foodGroups.isLoading}
              loading={foodGroups.isLoading}
            />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input />
          </Form.Item>

          {!isEditMode && (
            <Form.Item name="addMealItem" initialValue={true} valuePropName="checked">
              <Checkbox>Create meal item for this ingredient</Checkbox>
            </Form.Item>
          )}

          <Divider />

          <Form.Item label="USDA Food">
            <Select
              showSearch
              labelInValue
              filterOption={false}
              onSearch={(query) => {
                searchUSDAFood.current.next(query);
              }}
              loading={usdaSearching}
              dropdownRender={(defaultRender) =>
                usdaSearching ? (
                  <Flex gap="small" align="center" style={{ paddingInline: 12 }}>
                    <LoaderCircle color={token.colorTextDisabled} />
                    <Typography.Text disabled>Looking up...</Typography.Text>
                  </Flex>
                ) : (
                  defaultRender
                )
              }
              value={selectedUsdaFood?.fdcId}
              notFoundContent="Type something to lookup..."
              style={{ height: 50, borderRadius: token.borderRadiusLG }}
              options={usdaFoodOptions}
              // optionRender={(option) => option.label}
              onChange={(value, label) => {
                setSelectedUsdaFood(label);
              }}
            />
          </Form.Item>

          <Form.Item>
            <Descriptions
              labelStyle={{ color: token.colorText }}
              bordered
              column={1}
              size="small"
              items={[
                {
                  key: 1,
                  label: 'Data Type',
                  children: selectedUsdaFood?.dataType ?? '-',
                },
                {
                  key: 2,
                  label: 'Brand owner',
                  children: selectedUsdaFood?.brandOwner ?? '-',
                },
                {
                  key: 3,
                  label: 'Food category',
                  children: (
                    <>
                      {selectedUsdaFood?.foodCategory
                        ?.toString()
                        .split(',')
                        .map((fc: string, ind: number) => (
                          <Tag color="processing" key={ind}>
                            {fc}
                          </Tag>
                        )) ?? '-'}
                    </>
                  ),
                },
                {
                  key: 4,
                  label: 'Serving size',
                  children: selectedUsdaFood?.servingSize
                    ? selectedUsdaFood.servingSize.toFixed(2) +
                      ' ' +
                      selectedUsdaFood.servingSizeUnit
                    : '-',
                },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Table
              size="small"
              columns={[
                {
                  dataIndex: 'nutrientName',
                  title: 'Nutrient',
                },
                {
                  dataIndex: 'value',
                  title: 'Value',
                  width: '20%',
                  render: (value, record: any) => (
                    <>
                      {Number(value).toLocaleString('en-US', {
                        maximumFractionDigits: 2,
                      })}{' '}
                      {record.unitName.toLowerCase()}
                    </>
                  ),
                },
              ]}
              dataSource={selectedUsdaFood?.foodNutrients ?? []}
              pagination={false}
            />
          </Form.Item>

          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Dropdown
                menu={{
                  items: [
                    {
                      label: `Save`,
                      key: '1',
                      onClick: () => {
                        onSubmit({ newIngredient: false, ...formInstance.getFieldsValue() });
                      },
                    },
                    {
                      label: `Save and Create new one`,
                      key: '2',
                      onClick: () => {
                        onSubmit({ newIngredient: true, ...formInstance.getFieldsValue() });
                      },
                    },
                  ],
                }}
              >
                <Button
                  block
                  icon={<ChevronDown />}
                  type="primary"
                  disabled={foodGroups.isLoading || ingredients.isLoading}
                  loading={ingredients.isLoading}
                >
                  Save
                </Button>
              </Dropdown>

              <Button block onClick={onCancel} disabled={ingredients.isLoading}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

export default IngredientForm;
